export interface BackgroundPhoto {
  id: number
  photographer: string
}

const MAX_MANIFEST_BYTES = 32 * 1024
const MAX_PHOTOS = 20
let backgroundPromise: Promise<BackgroundPhoto | null> | undefined

export function backgroundImageUrl(id: number): string {
  return `https://cos-125-tp-cdn.794td.cn/photos/${id}.jpg?imageMogr2/thumbnail/1600x/format/webp/quality/78`
}

export function parseBackgroundManifest(value: unknown): BackgroundPhoto[] {
  if (!value || typeof value !== 'object') return []
  const manifest = value as Record<string, unknown>
  if (manifest.version !== 1 || !Array.isArray(manifest.photos) || manifest.photos.length > MAX_PHOTOS) return []
  if (manifest.updatedAt !== null && (typeof manifest.updatedAt !== 'string'
    || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(manifest.updatedAt)
    || !Number.isFinite(Date.parse(manifest.updatedAt)))) return []

  const photos: BackgroundPhoto[] = []
  const ids = new Set<number>()
  for (const value of manifest.photos) {
    if (!value || typeof value !== 'object') return []
    const photo = value as Record<string, unknown>
    if (typeof photo.id !== 'number' || !Number.isSafeInteger(photo.id) || photo.id <= 0 || ids.has(photo.id)) return []
    if (typeof photo.photographer !== 'string' || photo.photographer.length > 200) return []
    ids.add(photo.id)
    photos.push({ id: photo.id, photographer: photo.photographer.trim() })
  }
  return photos
}

async function fetchBackgroundPhotos(): Promise<BackgroundPhoto[]> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 5000)
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined
  try {
    const response = await fetch('/backgrounds.json', {
      credentials: 'omit', redirect: 'error', signal: controller.signal,
      headers: { Accept: 'application/json' },
    })
    if (!response.ok || !response.headers.get('content-type')?.toLowerCase().startsWith('application/json')) return []
    const declaredLength = Number(response.headers.get('content-length'))
    if (declaredLength > MAX_MANIFEST_BYTES || !response.body) return []
    reader = response.body.getReader()
    const decoder = new TextDecoder()
    let text = ''
    let bytes = 0
    while (bytes <= MAX_MANIFEST_BYTES) {
      const chunk = await reader.read()
      if (chunk.done) break
      bytes += chunk.value.byteLength
      if (bytes > MAX_MANIFEST_BYTES) return []
      text += decoder.decode(chunk.value, { stream: true })
    }
    text += decoder.decode()
    return parseBackgroundManifest(JSON.parse(text))
  } catch {
    return []
  } finally {
    clearTimeout(timeout)
    controller.abort()
    if (reader) {
      void reader.cancel().catch(() => undefined)
      reader.releaseLock()
    }
  }
}

function preloadBackground(photo: BackgroundPhoto): Promise<BackgroundPhoto | null> {
  return new Promise(resolve => {
    const image = new Image()
    let settled = false
    const finish = (loaded: boolean) => {
      if (settled) return
      settled = true
      clearTimeout(timeout)
      image.onload = null
      image.onerror = null
      if (!loaded) image.removeAttribute('src')
      resolve(loaded ? photo : null)
    }
    const timeout = setTimeout(() => finish(false), 10000)
    image.onload = () => finish(image.naturalWidth > 0 && image.naturalHeight > 0)
    image.onerror = () => finish(false)
    image.referrerPolicy = 'no-referrer'
    image.src = backgroundImageUrl(photo.id)
  })
}

// One selection and preload per full page visit, including React StrictMode
// effect replay. Routing and locale updates reuse this same settled promise.
export function loadBackgroundPhoto(): Promise<BackgroundPhoto | null> {
  backgroundPromise ||= fetchBackgroundPhotos().then(photos => {
    if (!photos.length) return null
    return preloadBackground(photos[Math.floor(Math.random() * photos.length)])
  }).catch(() => null)
  return backgroundPromise
}
